type MenuSoundKind = 'hover' | 'select' | 'confirm' | 'back' | 'toggle';

const STORAGE_KEY = 'tst:ui-volume';
const DEFAULT_VOLUME = 0.55;

class MenuSoundSystem {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private volume = readVolume();

  getVolume(): number {
    return this.volume;
  }

  setVolume(value: number): void {
    this.volume = Math.max(0, Math.min(1, value));
    writeStoredVolume(this.volume);
    if (this.master) {
      this.master.gain.setTargetAtTime(this.volume * 0.18, this.ctx?.currentTime ?? 0, 0.015);
    }
  }

  /**
   * Create the AudioContext (if needed) and resume it. Mobile Safari
   * keeps a freshly-created context in `suspended` until a user gesture
   * resumes it; calling this from a pointerdown / touchstart handler
   * means the very first menu sound (or the first race ignition) plays
   * without a delay or a swallowed cue.
   */
  unlock(): void {
    const ctx = this.ensureContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume();
  }

  play(kind: MenuSoundKind): void {
    const ctx = this.ensureContext();
    if (!ctx || !this.master || this.volume <= 0) return;

    if (ctx.state === 'suspended') {
      void ctx.resume();
    }

    if (kind === 'hover') this.hover(ctx);
    else if (kind === 'confirm') this.confirm(ctx);
    else if (kind === 'back') this.back(ctx);
    else if (kind === 'toggle') this.toggle(ctx);
    else this.select(ctx);
  }

  private ensureContext(): AudioContext | null {
    const AudioCtx = window.AudioContext ?? window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (this.ctx && this.master) return this.ctx;

    this.ctx = new AudioCtx();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.volume * 0.18;
    this.master.connect(this.ctx.destination);
    return this.ctx;
  }

  private hover(ctx: AudioContext): void {
    this.tone(ctx, {
      type: 'triangle',
      startFreq: 1280,
      endFreq: 1720,
      start: 0,
      duration: 0.045,
      gain: 0.22,
      detune: -5,
    });
  }

  private select(ctx: AudioContext): void {
    this.tone(ctx, {
      type: 'square',
      startFreq: 520,
      endFreq: 740,
      start: 0,
      duration: 0.07,
      gain: 0.28,
      detune: -8,
    });
    this.tone(ctx, {
      type: 'triangle',
      startFreq: 1040,
      endFreq: 1480,
      start: 0.018,
      duration: 0.08,
      gain: 0.2,
      detune: 10,
    });
  }

  private confirm(ctx: AudioContext): void {
    this.tone(ctx, {
      type: 'sawtooth',
      startFreq: 220,
      endFreq: 880,
      start: 0,
      duration: 0.18,
      gain: 0.26,
      detune: -12,
      filterStart: 620,
      filterEnd: 2600,
    });
    this.tone(ctx, {
      type: 'triangle',
      startFreq: 660,
      endFreq: 1320,
      start: 0.055,
      duration: 0.12,
      gain: 0.2,
      detune: 12,
    });
    this.noiseBurst(ctx, 0.018, 0.1, 0.035);
  }

  private back(ctx: AudioContext): void {
    this.tone(ctx, {
      type: 'triangle',
      startFreq: 360,
      endFreq: 130,
      start: 0,
      duration: 0.13,
      gain: 0.3,
      detune: -4,
      filterStart: 1400,
      filterEnd: 380,
    });
  }

  private toggle(ctx: AudioContext): void {
    this.tone(ctx, {
      type: 'square',
      startFreq: 920,
      endFreq: 420,
      start: 0,
      duration: 0.055,
      gain: 0.18,
      detune: -35,
    });
    this.tone(ctx, {
      type: 'square',
      startFreq: 980,
      endFreq: 520,
      start: 0.018,
      duration: 0.055,
      gain: 0.16,
      detune: 35,
    });
  }

  private tone(ctx: AudioContext, opts: ToneOpts): void {
    if (!this.master) return;
    const start = ctx.currentTime + opts.start;
    const end = start + opts.duration;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = opts.type;
    osc.frequency.setValueAtTime(opts.startFreq, start);
    osc.frequency.exponentialRampToValueAtTime(opts.endFreq, end);
    osc.detune.value = opts.detune ?? 0;

    filter.type = 'lowpass';
    filter.Q.value = 5.5;
    filter.frequency.setValueAtTime(opts.filterStart ?? 1800, start);
    filter.frequency.exponentialRampToValueAtTime(opts.filterEnd ?? 3000, end);

    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(opts.gain, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    osc.start(start);
    osc.stop(end + 0.02);
  }

  private noiseBurst(ctx: AudioContext, startOffset: number, duration: number, gainValue: number): void {
    if (!this.master) return;
    const sampleCount = Math.max(1, Math.floor(ctx.sampleRate * duration));
    const buffer = ctx.createBuffer(1, sampleCount, ctx.sampleRate);
    const samples = buffer.getChannelData(0);
    for (let i = 0; i < samples.length; i += 1) {
      samples[i] = (Math.random() * 2 - 1) * (1 - i / samples.length);
    }

    const start = ctx.currentTime + startOffset;
    const source = ctx.createBufferSource();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    source.buffer = buffer;
    filter.type = 'bandpass';
    filter.frequency.value = 2200;
    filter.Q.value = 3.5;
    gain.gain.setValueAtTime(gainValue, start);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    source.start(start);
    source.stop(start + duration);
  }
}

interface ToneOpts {
  type: OscillatorType;
  startFreq: number;
  endFreq: number;
  start: number;
  duration: number;
  gain: number;
  detune?: number;
  filterStart?: number;
  filterEnd?: number;
}

export const menuSounds = new MenuSoundSystem();

export function attachMenuSounds(root: HTMLElement): void {
  root.addEventListener('mouseover', (event) => {
    const target = findSoundTarget(event.target);
    if (!target || target.disabled || containsRelatedTarget(target, event)) return;
    menuSounds.play('hover');
  });

  root.addEventListener('click', (event) => {
    const target = findSoundTarget(event.target);
    if (!target || target.disabled) return;
    menuSounds.play(soundForTarget(target));
  }, { capture: true });

  root.addEventListener('change', (event) => {
    const target = findSoundTarget(event.target);
    if (!target || target.disabled || target.tagName === 'BUTTON') return;
    menuSounds.play('toggle');
  });
}

function readVolume(): number {
  const raw = readStoredVolume();
  if (raw === null) return DEFAULT_VOLUME;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(1, parsed)) : DEFAULT_VOLUME;
}

function readStoredVolume(): string | null {
  try {
    return typeof localStorage?.getItem === 'function'
      ? localStorage.getItem(STORAGE_KEY)
      : null;
  } catch {
    return null;
  }
}

function writeStoredVolume(value: number): void {
  try {
    if (typeof localStorage?.setItem === 'function') {
      localStorage.setItem(STORAGE_KEY, String(value));
    }
  } catch {
    // Some embedded/test environments expose storage but reject writes.
  }
}

function findSoundTarget(target: EventTarget | null): HTMLButtonElement | HTMLInputElement | null {
  if (!(target instanceof Element)) return null;
  return target.closest('button, input') as HTMLButtonElement | HTMLInputElement | null;
}

function containsRelatedTarget(target: HTMLElement, event: MouseEvent): boolean {
  return event.relatedTarget instanceof Node && target.contains(event.relatedTarget);
}

function soundForTarget(target: HTMLButtonElement | HTMLInputElement): MenuSoundKind {
  if (target instanceof HTMLInputElement) return 'toggle';
  const action = target.dataset.action ?? target.dataset.tab ?? '';
  if (action === 'race' || action === 'resume' || action === 'save' || action === 'send') return 'confirm';
  if (action === 'close' || action === 'quit') return 'back';
  if (action === 'fullscreen' || target.id === 'camera-toggle') return 'toggle';
  return 'select';
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}
